/**
 * Shows a live summary of every selected option across the main product
 * (native options), product group options (metaobject), add-ons (metafield)
 * and bundle items (metafield) e.g. "Größe: 2,5 Sitzer | Farbe: Beige".
 *
 * Rendered by the Selected options summary block (_product-selected-options-summary.liquid),
 * in the Product form or the Add to cart bar. Rebuilds
 * its text from every [data-option-label-row] (label + selected value) pair
 * already kept live by product-option.js, so bundle/add-on changes (which don't
 * trigger a section re-render) still work. Options hidden from the shopper
 * (Option - Preselect / Option - Visibility) are left out.
 */
class ProductSelectedOptionsSummary extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.target = this.querySelector('[data-selected-options-summary]') || this;
    this.update = this.update.bind(this);

    // Build the initial summary from the as-rendered page
    this.update();

    // Native variant + product group changes trigger a section re-render
    this.unsubscribeVariantChange = subscribe(`variant:changed:${this.sectionId}`, this.update);
    this.unsubscribeProductChange = subscribe(`product:changed:${this.sectionId}`, this.update);

    // Bundle/add-on changes update client-side only, so listen for those too
    this.unsubscribeBundleVariantChange = subscribe(
      `bundle:variant:changed:${this.sectionId}`,
      this.update
    );
    this.unsubscribeAddOnVariantChange = subscribe(
      `add-on:variant:changed:${this.sectionId}`,
      this.update
    );
  }

  disconnectedCallback() {
    this.unsubscribeVariantChange?.();
    this.unsubscribeProductChange?.();
    this.unsubscribeBundleVariantChange?.();
    this.unsubscribeAddOnVariantChange?.();
  }

  /**
   * Rebuild the summary text from every option's label + currently selected
   * value, scoped to this product form's section. Each [data-option-label-row]
   * is rendered once per option group (not once per value), so there's no
   * dedupe/collision concern between options that happen to share a name
   * across the main product, group, bundle and add-on items.
   */
  update() {
    // Wait a tick for subscribers that swap the DOM (e.g. ProductForm) to finish first
    requestAnimationFrame(() => {
      let summary = '';

      try {
        const scope =
          document.querySelector(`product-form[data-section-id="${this.sectionId}"]`) || document;
        const entries = [];

        scope.querySelectorAll('[data-option-label-row]').forEach((row) => {
          // Skip options hidden from the shopper, e.g. bundle/add-on options locked by
          // "Option - Preselect" or hidden by "Option - Visibility". They're enforced,
          // not configured, so they don't belong in the summary.
          if (this.isHiddenOption(row)) return;

          const label = row.querySelector('[data-option-label]')?.textContent?.trim();
          const valueEl = row.querySelector('[data-selected-value]');
          const value = valueEl?.textContent?.trim();
          // Skip options with nothing selected yet, and "none" bundle/add-on choices
          // (data-none-selected is authoritative, kept live by product-option.js; it
          // covers both the initial server render and any client-side change, unlike
          // matching on the text itself)
          if (!label || !value || valueEl?.dataset.noneSelected === 'true') return;
          entries.push(`${label}: ${value}`);
        });

        summary = entries.join(' | ');
      } catch (error) {
        // A bad row shouldn't permanently stick the element at display:none
        console.error('product-selected-options-summary:', error);
      }

      this.target.textContent = summary;
      // Set display directly (rather than toggling a "hidden" class) so it never
      // fights the element's own "flex"/"block" display utility on specificity/order.
      this.style.display = summary === '' ? 'none' : '';

      // Also hide the theme block wrapper so an empty summary doesn't leave a gap in the flex layout
      const blockWrapper = this.closest('[id^="shopify-block-"]');
      if (blockWrapper) blockWrapper.style.display = summary === '' ? 'none' : '';
    });
  }

  /**
   * Whether an option row is hidden from the shopper. The option blocks hide these
   * server side with a "hidden" class on the fieldset, and hide the whole block
   * wrapper (display: none) when none of its options are visible. Collapsed
   * collapsible groups are not matched, those options are still configurable.
   * @param {HTMLElement} row - The [data-option-label-row] element
   * @returns {Boolean}
   */
  isHiddenOption(row) {
    if (row.closest('fieldset.hidden, [hidden]')) return true;

    const blockWrapper = row.closest('[id^="shopify-block-"]');
    return blockWrapper ? getComputedStyle(blockWrapper).display === 'none' : false;
  }
}

customElements.define('product-selected-options-summary', ProductSelectedOptionsSummary);
